import { Component, Input } from '@angular/core';
import { ChatFormComponent } from '../chat-form/chat-form.component';
import { ChatReplyComponent } from '../chat-reply/chat-reply.component';
import { ExpansionPanelComponent } from '../expansion-panel/expansion-panel.component';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { ChatState } from '../../../store/chat/chat.model';
import { storeChatData, clearChatData, setLoading } from '../../../store/chat/chat.actions';
import { HttpRequestService } from '../../../utilities/http/request.utilities';
import { environment } from '../../../../environments/environment';
import { WebsocketService } from '../../../services/websocket/websocket.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { Subject, BehaviorSubject } from 'rxjs';
import { first, map, takeUntil, tap } from 'rxjs/operators';
import { EquipmentState } from '../../../store/equipment/equipment.model';
import { UserState } from '../../../store/user/user.model';
import { firstValueFrom } from 'rxjs';
import { AppLauncherInfo } from '../../../store/appLauncher/appLauncher.model';


@Component({
  selector: 'app-chat-panel',
  standalone: true,
  imports: [ChatFormComponent, ExpansionPanelComponent, ChatReplyComponent, CommonModule],
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.css']
})
export class ChatPanelComponent {
  public isLoading$ = this.store.select(state => state.chat.isLoading).pipe(
    map(isLoading => isLoading !== null && isLoading !== undefined ? isLoading : false)
  );
  public isReply = new BehaviorSubject<boolean>(false);
  public isError = new BehaviorSubject<boolean>(false);
  public showGeneralAIButton = new BehaviorSubject<boolean>(false);
  public genericReplyText = new BehaviorSubject<string | null>(null);
  public isGenericLoading = new BehaviorSubject<boolean>(false);  
  public message: string = '';
  public transactionId: string = '';

  private stopMessages$ = new Subject<void>();
  public fullMessageText = new BehaviorSubject<string>('');
  // This holds the complete raw text as it comes in from the server
  private rawMessageBuffer: string = '';
  // Buffer for incomplete tag fragments at the end of a chunk
  private partialChunkBuffer: string = '';
  // Buffer for potential section headers split across chunks
  private potentialSectionHeader: string = '';
  // Tracks the last event_type received to inject section headers on change
  private lastEventType: string = '';
  public resources = new BehaviorSubject<Array<{ title: string, url: string, message: string, page: number, downloadUrl: string}>>([]);
  public videoResources = new BehaviorSubject<Array<{ title: string, docPath: string, message: string, offset: string }>>([]);
  public safetyText = new BehaviorSubject<string>('');
  public safetyGenericText = new BehaviorSubject<string>('');
  public videoAnswerText = new BehaviorSubject<string>('');

  private webAiEnabled = true;

  constructor(
    private httpRequestService: HttpRequestService,
    private store: Store<{ chat: ChatState, equipment: EquipmentState, user: UserState, appLauncher: AppLauncherInfo }>,
    private websocketService: WebsocketService,
    private featureFlagService: FeatureFlagService
  ) {
    this.featureFlagService.webAiEnabled$.subscribe(v => this.webAiEnabled = v);
  }

  public async submitQuestion(message: string): Promise<void> {
    this.isError.next(false);
    this.resources.next([]);
    this.videoResources.next([]);
    this.fullMessageText.next('');
    this.videoAnswerText.next('');
    this.safetyText.next('');
    this.safetyGenericText.next('');
    this.showGeneralAIButton.next(false);
    this.genericReplyText.next(null);
    this.isGenericLoading.next(false);
    this.rawMessageBuffer = '';
    this.partialChunkBuffer = '';
    this.potentialSectionHeader = '';
    this.lastEventType = '';

    try {
      this.message = message;
      const transactionId = await this.fetchMessageApi(this.message);

      if (!transactionId) {
        throw new Error('Failed to fetch transaction id');
      }

      this.transactionId = transactionId;

      this.store.dispatch(clearChatData());
      this.store.dispatch(storeChatData({ chat: { transactionId } }));

      this.websocketService.createWebSocketService();
      this.websocketService.getMessages().pipe(
        tap((message: any) => {
          // Handle special messages
          if (!message || !message.response) {
            console.warn('[ChatPanelComponent] Received message with missing response:', message);
            return;
          }

          if (typeof message.response === 'string' && (message.response.startsWith('http://') || message.response.startsWith('https://'))) {
            const messageUrl = message.response;

            const fileNameMatch = messageUrl.match(/\/([^\/\?]+\.pdf)/i);
            const fileNameFromUrl = fileNameMatch?.[1];
            if (!fileNameFromUrl) return;

            const urlAlreadyApplied = this.resources.value.some(r =>
              r.url === messageUrl && r.title.toLowerCase() === fileNameFromUrl.toLowerCase()
            );

            const updatedResources = this.resources.value.map(resource => {
              const isSameFile = resource.title.toLowerCase() === fileNameFromUrl.toLowerCase();
              if (isSameFile && (resource.url === '' || !urlAlreadyApplied)) {
                return { ...resource, url: messageUrl };
              }
              return resource;
            });

            this.resources.next(updatedResources);
            const cloudfrontResource = updatedResources.find(r =>
              r.url?.includes('cloudfront') && r.title.toLowerCase() === fileNameFromUrl?.toLowerCase()
            );

            if (cloudfrontResource) {
                updatedResources.map(resource => {
                const isSameFile = resource.title.toLowerCase() === cloudfrontResource.title.toLowerCase();
                const isUrlMissingOrDifferent = resource.url === '' || resource.url !== cloudfrontResource.url;
                if (isSameFile && isUrlMissingOrDifferent) {
                  return { ...resource, url: cloudfrontResource.url };
                }
                return resource;
              });

              this.resources.next(updatedResources);
              setTimeout(() => {
                const currentResources = this.resources.value;
                const firstWithUrl = currentResources.find(r => r.url?.includes('cloudfront') && r.url !== '');
                if (firstWithUrl) {
                  const updated = currentResources.map(r => {
                    const needsUpdate = r.url === '' && r.title === firstWithUrl.title;
                    return needsUpdate ? { ...r, url: firstWithUrl.url } : r;
                  });
                  this.resources.next(updated);
                }
              }, 0);
            }
            return;
          }
          console.log('Received message chunk:', message.response);

          // If the message has an event_type, inject the section header when it changes
          if (message.event_type && message.event_type !== this.lastEventType) {
            this.lastEventType = message.event_type;
            const header = message.event_type.startsWith('#')
              ? message.event_type
              : `# ${message.event_type}`;
            this.rawMessageBuffer += `\n${header}\n`;
          }

          // Check for incomplete XML tags at the end of the incoming chunk
          let processedChunk = this.partialChunkBuffer + message.response;
          this.partialChunkBuffer = '';

          // Check if the chunk ends with an incomplete tag
          const lastOpenBracket = processedChunk.lastIndexOf('<');
          if (lastOpenBracket !== -1) {
            const lastCloseBracket = processedChunk.lastIndexOf('>');
            // If the last open bracket is after the last close bracket, we have an incomplete tag
            if (lastOpenBracket > lastCloseBracket) {
              this.partialChunkBuffer = processedChunk.substring(lastOpenBracket);
              processedChunk = processedChunk.substring(0, lastOpenBracket);
            }
          }

          // Check if we have a potential section header from previous chunk
          if (this.potentialSectionHeader) {
            processedChunk = this.potentialSectionHeader + processedChunk;
            this.potentialSectionHeader = '';
          }

          // Check if this chunk ends with a potential section header (e.g. "# REFERE" split across chunks).
          // Only match when preceded by "# " to avoid stripping body text that happens to match a header word.
          const knownHeaders = ['MANUAL ANSWERS', 'VIDEO ANSWERS', 'VIDEO_REFERENCES', 'REFERENCE_LINKS', 'REFERENCES', 'REFERENCE', 'ANSWERS', 'ANSWER', 'PARTS', 'SAFETY', 'ANSWERABLE', 'DONE'];
          let headerMatchFound = false;
          for (const header of knownHeaders) {
            if (headerMatchFound) break;
            for (let i = 1; i <= header.length; i++) {
              const partialHeader = header.substring(0, i);
              const candidate = `# ${partialHeader}`;
              if (processedChunk.endsWith(candidate)) {
                this.potentialSectionHeader = candidate;
                processedChunk = processedChunk.substring(0, processedChunk.length - candidate.length);
                headerMatchFound = true;
                break;
              }
            }
          }

          // Append to the raw buffer and reprocess
          this.rawMessageBuffer += processedChunk;
          this.processMessageBuffer();
        }),
        takeUntil(this.stopMessages$)
      ).subscribe(message => {
        if (message && message.is_final) {
          this.stopMessages$.next();
          this.checkForFallbackEligibility();
        }
      });

    } catch (error) {
      this.isError.next(true);
    }
  }

  /**
   * Process the entire message buffer to extract ANSWERS and REFERENCES sections
   */
  private processMessageBuffer(): void {
    // First, display only the ANSWERS section
    this.updateDisplayedText();

    // Then, process any references
    this.processReferences();

    this.processParts()

    this.processVideoReferences()

    this.processSafety()

    // Use the same regex as extractReferencesSection for consistency
    const hasReferences = /REFERENCES\s*:/i.test(this.rawMessageBuffer);
    console.log(`Current raw buffer length: ${this.rawMessageBuffer.length}, Has REFERENCES: ${hasReferences}`);
  }

  /**
   * Extract and display only the ANSWERS section from the raw buffer
   */
  private updateDisplayedText(): void {
    const answersText = this.extractAnswersSection(this.rawMessageBuffer);
    if (answersText) {
      this.fullMessageText.next(this.sanitizeDisplayText(answersText));
      this.isReply.next(true);
    }
    const videoAnswerText = this.extractVideoAnswersSection(this.rawMessageBuffer);
    if (videoAnswerText) {
      this.videoAnswerText.next(this.sanitizeDisplayText(videoAnswerText));
      this.isReply.next(true);
    } else {
      this.videoAnswerText.next('');
    }
    
    // If no section headers found but we have content, display it directly.
    // This handles fallback error messages that arrive without # ANSWERS headers.
    if (!answersText && !videoAnswerText && this.rawMessageBuffer.trim()) {
      this.fullMessageText.next(this.sanitizeDisplayText(this.rawMessageBuffer));
      this.isReply.next(true);
    }
  }

  private sanitizeDisplayText(text: string): string {
    return text
      .replace(/<reference>[\s\S]*?<\/reference>/g, '')
      .replace(/<done\s*\/?>\s*(<\/done>)?/g, '')
      .replace(/<complete\s*\/?>/g, '')
      .replace(/^#?\s*(MANUAL ANSWERS|VIDEO ANSWERS|ANSWERS|REFERENCES|REFERENCE_LINKS|VIDEO_REFERENCES|PARTS|SAFETY|ANSWERABLE|DONE)\s*:?\s*$/gm, '')
      .trim();
  }

  /**
   * Extract just the MANUAL ANSWERS section from text
   */
  private extractAnswersSection(text: string): string {
    // Handles both legacy '# ANSWERS' and new '# MANUAL ANSWERS' formats
    // # is required to avoid matching the word 'answers' in body text
    const answersRegex = /#\s*(?:MANUAL\s+)?ANSWERS\s*:?/i;
    const videoAnswersRegex = /#\s*VIDEO\s+ANSWERS\s*:?/i;
    const referencesRegex = /#\s*REFERENCES\s*:?/i;
    const safetyRegex = /^#?\s*SAFETY\s*:/im;

    if (!answersRegex.test(text)) return ''; // Don't display raw buffer when header hasn't arrived yet

    const answersMatch = text.match(answersRegex);
    if (!answersMatch) return text;

    const answersStart = answersMatch.index! + answersMatch[0].length;

    // Find the earliest stop boundary after answersStart
    let endIndex = text.length;

    const videoAnswersMatch = text.match(videoAnswersRegex);
    if (videoAnswersMatch && videoAnswersMatch.index! > answersStart && videoAnswersMatch.index! < endIndex) {
      endIndex = videoAnswersMatch.index!;
    }
    const referencesMatch = text.match(referencesRegex);
    if (referencesMatch && referencesMatch.index! > answersStart && referencesMatch.index! < endIndex) {
      endIndex = referencesMatch.index!;
    }
    const safetyMatch = text.match(safetyRegex);
    if (safetyMatch && safetyMatch.index! > answersStart && safetyMatch.index! < endIndex) {
      endIndex = safetyMatch.index!;
    }

    return text.substring(answersStart, endIndex).trim();
  }

  /**
   * Extract just the VIDEO ANSWERS section from text
   */
  private extractVideoAnswersSection(text: string): string {
    // # is required to avoid matching the word 'references' in body text
    const videoAnswersRegex = /#\s*VIDEO\s+ANSWERS\s*:?/i;
    const videoReferencesRegex = /#\s*VIDEO_REFERENCES\s*:?/i;
    const referencesRegex = /#\s*REFERENCES\s*:?/i;
    const safetyRegex = /^#?\s*SAFETY\s*:/im;

    const videoAnswersMatch = text.match(videoAnswersRegex);
    if (!videoAnswersMatch) return '';

    const start = videoAnswersMatch.index! + videoAnswersMatch[0].length;

    // Find the earliest stop boundary: VIDEO_REFERENCES, REFERENCES, or SAFETY
    let endIndex = text.length;

    const videoReferencesMatch = text.match(videoReferencesRegex);
    if (videoReferencesMatch && videoReferencesMatch.index! > start) {
      endIndex = videoReferencesMatch.index!;
    }

    const referencesMatch = text.match(referencesRegex);
    if (referencesMatch && referencesMatch.index! > start && referencesMatch.index! < endIndex) {
      endIndex = referencesMatch.index!;
    }

    const safetyMatch = text.match(safetyRegex);
    if (safetyMatch && safetyMatch.index! > start && safetyMatch.index! < endIndex) {
      endIndex = safetyMatch.index!;
    }

    const extracted = text.substring(start, endIndex).trim();

    // Normalize whitespace for checking (handle various line breaks and spaces)
    const normalized = extracted.replace(/\s+/g, ' ').trim().toLowerCase();

    // List of placeholder messages indicating no video content
    const noContentMessages = [
      'no relevant video guidance found',
      'no relevant video',
      'no video guidance',
      'no videos found',
      'no video found',
      'no videos available',
      'no video available',
      'no relevant videos',
      'video guidance not available',
    ];

    // Check if the entire content is just a no-content placeholder
    if (noContentMessages.some(msg => normalized === msg || 
                                       (normalized.startsWith(msg) && extracted.length < 150))) {
      return '';
    }

    return extracted;
  }

  /**
   * Extract and process the REFERENCES section from the raw buffer
   */
  private processReferences(): void {
    const referencesText = this.extractReferencesSection(this.rawMessageBuffer);
     // Debug logging
     console.log(`Extracting references from: ${this.rawMessageBuffer.substring(0, 100)}...`);

    if (!referencesText) {
      console.log('No REFERENCES section found');
      return;
    }

    // Split the references by lines and process each
    const referenceLines = referencesText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    let lastDescription = '';
    for (let i = 0; i < referenceLines.length; i++) {
      const line = referenceLines[i];

      // Track non-reference lines as potential descriptions
      if (!line.includes('<reference>')) {
        const cleaned = line.replace(/^\d+\.\s*/, '').trim();
        if (cleaned) {
          lastDescription = cleaned;
        }
        continue;
      }

      // Extract the instruction text before the reference tag on the same line
      let instructionText = line.substring(0, line.indexOf('<reference>')).trim();
      instructionText = instructionText.replace(/^\d+\.\s*/, '').trim();

      // Fall back to the last-seen description line
      if (!instructionText) {
        instructionText = lastDescription;
      }

      // Extract document information using regex
      const refMatch = line.match(/<reference><document_name>([^<]+)<\/document_name><page>([\d,\s]+)<\/page><\/reference>/);

      if (refMatch) {
        const docPath = refMatch[1];
        const pageField = refMatch[2];

        const pathParts = docPath.split('/');
        let documentMake = 'unknown';
        let documentType = 'unknown';
        let documentFile = 'unknown';

        // Handle both old format (5 segments) and new format (4 segments)
        if (pathParts.length >= 5) {
          // Old format: manuals_by_type/en_US/DOUGLAS/ServiceManual/1312293.pdf
          documentMake = pathParts[2]?.trim() || 'unknown';
          documentType = pathParts[3]?.trim() || 'unknown';
          documentFile = pathParts[4]?.trim() || 'unknown';
        } else if (pathParts.length === 4) {
          // New format: manuals/en_US/GENIE/1312293.pdf
          documentMake = pathParts[2]?.trim() || 'unknown';
          documentType = pathParts[1]?.trim() || 'unknown';
          documentFile = pathParts[3]?.trim() || 'unknown';
        }

        const providerResourceUrl = `${environment.integrations.manualDownload}?equipment_make=${documentMake}&file_name=${encodeURIComponent(documentFile)}&manual_type=${documentType}`;

        const pageList = pageField.split(',').map(p => p.trim());
        for (const pageNum of pageList) {
          const parsedPage = parseInt(pageNum, 10);
          if (isNaN(parsedPage)) continue;
          const message = `${instructionText}`;
          const resource = {
            title: documentFile,
            url: '',
            message,
            page: parsedPage,
            downloadUrl: providerResourceUrl
          };

          const alreadyExists = this.resources.value.some(r =>
            r.title === resource.title &&
            r.page === resource.page &&
            r.downloadUrl === resource.downloadUrl &&
            r.message === resource.message
          );

          if (!alreadyExists) {
            this.resources.next([...this.resources.value, resource]);
          }
        }
      }
    }
  }

  private processVideoReferences(): void {
    const referencesText = this.extractVideoReferencesSection(this.rawMessageBuffer);
    console.log(`Raw video references ${this.rawMessageBuffer}`);
    console.log(`Extracting video references from buffer...`);

    if (!referencesText) {
      console.log('No VIDEO_REFERENCE_LINKS section found');
      return;
    }

    // Split the references by lines and process each
    const referenceLines = referencesText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    for (let i = 0; i < referenceLines.length; i++) {
      const line = referenceLines[i];
      // Skip lines that don't contain reference data
      if (!line.includes('<reference>')) continue;

      // Extract the instruction text before the reference tag on the same line
      let instructionText = line.substring(0, line.indexOf('<reference>')).trim();

      // New format: description is on the preceding line when <reference> starts at position 0
      if (!instructionText && i > 0) {
        instructionText = referenceLines[i - 1].replace(/^\d+\.\s*/, '').trim();
      } else {
        // Old format: description and reference on same line, strip leading number
        instructionText = instructionText.replace(/^\d+\.\s*/, '').trim();
      }

      // video_offsets uses time-range format (e.g. 00:02:31-00:02:37)
      const refMatch = line.match(/<reference><document_name>([^<]+)<\/document_name><video_offsets>([^<]+)<\/video_offsets><\/reference>/);

      if (refMatch) {
        console.log(`Found video reference: ${line}`);
        let docPath = refMatch[1];
        const videoOffset = refMatch[2].trim();

        // Note: docPath may contain full s3:// URI — backend handles the resolution

        const documentFile = docPath.split('/').pop()?.trim() || 'unknown';

        const resource = {
          title: documentFile,
          docPath,
          message: instructionText,
          offset: videoOffset,
        };

        const alreadyExists = this.videoResources.value.some(r =>
          r.docPath === resource.docPath &&
          r.offset === resource.offset &&
          r.message === resource.message
        );

        if (!alreadyExists) {
          this.videoResources.next([...this.videoResources.value, resource]);
        }
      }
    }
  }

  private extractVideoReferencesSection(text: string): string {
    const videoRefsPattern = /#\s*VIDEO_REFERENCES\s*:?/i;

    if (!videoRefsPattern.test(text)) {
      return '';
    }

    const videoRefsMatch = text.match(videoRefsPattern);
    if (!videoRefsMatch) return '';
    const videoRefsStart = videoRefsMatch.index! + videoRefsMatch[0].length;

    const videoLinksPattern = /#\s*VIDEO_REFERENCE_LINKS\s*:?/i;
    const donePattern = /#\s*DONE\s*:?/i;

    let endIndex = text.length;
    const videoLinksMatch = text.match(videoLinksPattern);
    if (videoLinksMatch && videoLinksMatch.index! > videoRefsStart) endIndex = Math.min(endIndex, videoLinksMatch.index!);
    const doneMatch = text.match(donePattern);
    if (doneMatch && doneMatch.index! > videoRefsStart) endIndex = Math.min(endIndex, doneMatch.index!);

    return text.substring(videoRefsStart, endIndex).trim();
  }

  /**
   * Extract just the REFERENCES section from text
   */
  private extractReferencesSection(text: string): string {
    // # is required to avoid matching the word 'references' in body text
    const referencesPattern = /#\s*REFERENCES\s*:?/i;

    if (!referencesPattern.test(text)) {
      console.log('No REFERENCES section found');
      return '';
    }

    const referencesMatch = text.match(referencesPattern);
    if (!referencesMatch) return '';
    const referencesStart = referencesMatch.index! + referencesMatch[0].length;

    // Stop at the first of: REFERENCE_LINKS, PARTS, VIDEO_REFERENCES, DONE
    const stopPatterns = [
      /#\s*REFERENCE_LINKS\s*:?/i,
      /#\s*PARTS\s*:/i,
      /#\s*VIDEO_REFERENCES\s*:?/i,
      /#\s*DONE\s*:?/i,
    ];
    let endIndex = text.length;
    for (const pattern of stopPatterns) {
      const m = text.match(pattern);
      if (m && m.index! > referencesStart) {
        endIndex = Math.min(endIndex, m.index!);
      }
    }
    return text.substring(referencesStart, endIndex).trim();

    // // If the matched pattern doesn't include a colon, we need to find or add it
    // if (!referencesMatch[0].includes(':')) {
    //   // If there's a colon after the match, include it
    //   if (text.charAt(referencesStart) === ':') {
    //     referencesStart++;
    //   }
    // }

    // return text.substring(referencesStart).trim();
  }


  private extractPartsSection(text: string): string {
    const partsPattern = /^#?\s*PARTS\s*:/im;

    if (!partsPattern.test(text)) {
      console.log('No PARTS section found');
      return '';
    }

    const partsMatch = text.match(partsPattern);
    if (!partsMatch) return '';
    let partsStart = partsMatch.index! + partsMatch[0].length;
    
    // If the matched pattern doesn't include a colon, we need to find or add it
    if (!partsMatch[0].includes(':')) {
      // If there's a colon after the match, include it
      if (text.charAt(partsStart) === ':') {
        partsStart++;
      }
    }

    return text.substring(partsStart).trim();
  }

  private processParts(): void {
    const referencesText = this.extractPartsSection(this.rawMessageBuffer);
     // Debug logging
     console.log(`Extracting parts from: ${this.rawMessageBuffer.substring(0, 100)}...`);

    if (!referencesText) {
      console.log('No PARTS section found');
      return;
    }

    // Split the references by lines and process each
    const referenceLines = referencesText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    let lastDescription = '';
    for (let i = 0; i < referenceLines.length; i++) {
      const line = referenceLines[i];

      // Track non-reference lines as potential descriptions
      if (!line.includes('<reference>')) {
        const cleaned = line.replace(/^\d+\.\s*/, '').trim();
        if (cleaned) {
          lastDescription = cleaned;
        }
        continue;
      }

      // Extract the instruction text before the reference tag on the same line
      let instructionText = line.substring(0, line.indexOf('<reference>')).trim();
      instructionText = instructionText.replace(/^\d+\.\s*/, '').trim();

      // Fall back to the last-seen description line
      if (!instructionText) {
        instructionText = lastDescription;
      }

      // Extract document information using regex
      const refMatch = line.match(/<reference><document_name>([^<]+)<\/document_name><page>([\d,\s]+)<\/page><\/reference>/);

      if (refMatch) {
        const docPath = refMatch[1];
        const pageField = refMatch[2];

        const pathParts = docPath.split('/');
        let documentMake = 'unknown';
        let documentType = 'unknown';
        let documentFile = 'unknown';

        // Handle both old format (5 segments) and new format (4 segments)
        if (pathParts.length >= 5) {
          // Old format: manuals_by_type/en_US/DOUGLAS/ServiceManual/1312293.pdf
          documentMake = pathParts[2]?.trim() || 'unknown';
          documentType = pathParts[3]?.trim() || 'unknown';
          documentFile = pathParts[4]?.trim() || 'unknown';
        } else if (pathParts.length === 4) {
          // New format: manuals/en_US/GENIE/1312293.pdf
          documentMake = pathParts[2]?.trim() || 'unknown';
          documentType = pathParts[1]?.trim() || 'unknown';
          documentFile = pathParts[3]?.trim() || 'unknown';
        }

        const providerResourceUrl = `${environment.integrations.manualDownload}?equipment_make=${documentMake}&file_name=${encodeURIComponent(documentFile)}&manual_type=${documentType}`;

        const pageList = pageField.split(',').map(p => p.trim());
        for (const pageNum of pageList) {
          const parsedPage = parseInt(pageNum, 10);
          if (isNaN(parsedPage)) continue;
          const message = `${instructionText}`;
          const resource = {
            title: documentFile,
            url: '',
            message,
            page: parsedPage,
            downloadUrl: providerResourceUrl
          };

          const alreadyExists = this.resources.value.some(r =>
            r.title === resource.title &&
            r.page === resource.page &&
            r.downloadUrl === resource.downloadUrl &&
            r.message === resource.message
          );

          if (!alreadyExists) {
            this.resources.next([...this.resources.value, resource]);
          }
        }
      }
    }
  }

  private extractSafetySection(text: string): string {
    const safetyPattern = /^#?\s*SAFETY\s*:/im;

    if (!safetyPattern.test(text)) {
      console.log('No SAFETY section found');
      return '';
    }

    const safetyMatch = text.match(safetyPattern);
    if (!safetyMatch) return '';
    let safetyStart = safetyMatch.index! + safetyMatch[0].length;

    // If the matched pattern doesn't include a colon, we need to find or add it
    if (!safetyMatch[0].includes(':')) {
      // If there's a colon after the match, include it
      if (text.charAt(safetyStart) === ':') {
        safetyStart++;
      }
    }

    return text.substring(safetyStart).trim();
  }

private processSafety(): void {
    const sectionText = this.extractSafetySection(this.rawMessageBuffer);
    console.log(`Extracting safety message from: ${this.rawMessageBuffer.substring(0, 100)}...`);
    if (!sectionText) {
      console.log('No SAFETY section found');
      return;
    }

    const safetyText = sectionText.trim();
    this.safetyText.next(safetyText);
    console.log('Safety Message:', safetyText);
  }

private processSafetyGeneric(): void {
    const sectionText = this.genericReplyText//this.extractSafetySection(this.rawMessageBuffer);
    console.log(`Deciding generic safety message from value of ${sectionText}`);
    if (!sectionText) {
      console.log('No generic response. No need for displaying generic safety message');
      return;
    }

    const safetyText = "When servicing and repairing equipment, ensure all equipment energy sources have been released or controlled using Lockout/tagout procedures to prevent injury.";
    this.safetyGenericText.next(safetyText);
    console.log('Safety Message:', safetyText);
  }

  private checkForFallbackEligibility(): void {
    if (!this.webAiEnabled) return;
    const text = this.fullMessageText.value;
    const isEmpty = !text || text.trim().length === 0;
    const answerableValue = this.extractAnswerableValue();

    if (isEmpty || answerableValue === 'NONE' || answerableValue === 'PARTIAL') {
      this.showGeneralAIButton.next(true);
    }
  }

  private extractAnswerableValue(): string | null {
    const answerableRegex = /#\s*ANSWERABLE\s*:?\s*/i;
    const match = this.rawMessageBuffer.match(answerableRegex);
    if (!match) return null;

    const start = match.index! + match[0].length;
    const remaining = this.rawMessageBuffer.substring(start).trim();
    const firstLine = remaining.split(/\r?\n/)[0].trim().toUpperCase();

    if (['FULL', 'PARTIAL', 'NONE'].includes(firstLine)) {
      return firstLine;
    }
    return null;
  }

  public async handleTryGeneralAI(): Promise<void> {
    this.showGeneralAIButton.next(false);
    this.isGenericLoading.next(true);
    try {
      const result = await new Promise<any>((resolve, reject) =>
        this.httpRequestService.request<any>('POST', `${environment.integrations.genericQuery}`, {
          original_transaction_id: this.transactionId,
        }).subscribe({ next: resolve, error: reject })
      );

      const newTransactionId = result.transaction_id;

      const txnResult = await new Promise<any>((resolve, reject) =>
        this.httpRequestService.request<any>('GET', `${environment.integrations.transactionFeedback}/${newTransactionId}`)
          .subscribe({ next: resolve, error: reject })
      );

      this.genericReplyText.next(txnResult.llm_response || 'Unable to generate a response.');
      this.processSafetyGeneric();
    } catch (error) {
      console.error('Generic AI query failed', error);
      this.genericReplyText.next('An error occurred. Please try again.');
    } finally {
      this.isGenericLoading.next(false);
    }
  }

  public clearReply(): void {
    this.stopMessages$.next();
    this.fullMessageText.next('');
    this.isReply.next(false);
    this.showGeneralAIButton.next(false);
    this.genericReplyText.next(null);
    this.isGenericLoading.next(false);
    this.store.dispatch(setLoading({ isLoading: false }));
    this.message = '';
    this.transactionId = '';
    this.rawMessageBuffer = '';
    this.partialChunkBuffer = '';
    this.potentialSectionHeader = '';
    this.lastEventType = '';
  }

  private async fetchMessageApi(message: string): Promise<any> {
    try {
      const make = await firstValueFrom(this.store.select(state => state.equipment.equipment.details.make));
      const model = await firstValueFrom(this.store.select(state => state.equipment.equipment.details.model));
      const userId = await firstValueFrom(this.store.select(state => state.user.userInfo?.id));
      const userEmail = await firstValueFrom(this.store.select(state => state.user.userInfo?.email));
      const equipmentCode = await firstValueFrom(this.store.select(state => state.equipment.equipment.details.equipmentCode));
      const equipmentSerialRange = await firstValueFrom(this.store.select(state => state.equipment.equipment.details.serialNumberRange));
      const punchOut = await firstValueFrom(this.store.select(state => state.appLauncher?.user));

      let user = ''
      if(punchOut){
        console.log('This request is coming from punch out')
        user = punchOut;
      }
      else {
        user = userId || '';
      }

      if (!userId && !punchOut) {
        throw new Error('User ID is null');
      }
      if (!userEmail && !punchOut) {
        throw new Error('User Email is null');
      }
      // let username: string = userEmail.split('@')[0];
      
      let selectedRange = null;
      if (equipmentSerialRange) {
        selectedRange = `${model}#${equipmentSerialRange}`;
      }
      return await new Promise((resolve, reject) =>
        this.httpRequestService.request<any>('POST', `${environment.integrations.chatQuestionData}`, {
          question: message,
          user,
          // username, No Longer Need since we pull from header in TL
          equipmentCode,
          modelStartEnd: selectedRange,
          make,
        }).subscribe({
          next: (response) => {
            if (!response.transaction_id) {
              return reject(null);
            }
            resolve(response.transaction_id);
          },
          error: (error) => {
            console.error('Failed to fetch transaction id', error);
            reject(null);
          }
        }));
      
    } catch (error) {
      console.error('Failed to fetch transaction id', error);
      return null;
    }
  }
}
